/**
 * PaperTrading Component
 * Location: frontend/src/components/PaperTrading.js
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  History,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  X,
  Trophy,
  Target,
  Percent,
  Clock,
  ExternalLink
} from 'lucide-react';
import { PaperTradingService } from '../services/paperTradingService';
import './PaperTrading.css';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatPercent = (value) => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// =============================================================================
// TRADE PANEL
// =============================================================================
function TradePanel({ onTradeComplete, initialSymbol = '' }) {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [type, setType] = useState('buy');
  const [inputMode, setInputMode] = useState('shares');
  const [quantity, setQuantity] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialSymbol) {
      setSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  const popularAssets = [
    { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
    { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
    { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
    { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
    { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
    { symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock' },
    { symbol: 'V', name: 'Visa Inc.', type: 'stock' },
    { symbol: 'WMT', name: 'Walmart Inc.', type: 'stock' },
    { symbol: 'BTC-USD', name: 'Bitcoin USD', type: 'crypto' },
    { symbol: 'ETH-USD', name: 'Ethereum USD', type: 'crypto' },
    { symbol: 'SOL-USD', name: 'Solana USD', type: 'crypto' },
    { symbol: 'XRP-USD', name: 'Ripple USD', type: 'crypto' },
    { symbol: 'DOGE-USD', name: 'Dogecoin USD', type: 'crypto' },
  ];

  useEffect(() => {
    if (symbol.length === 0) {
      setSearchResults(popularAssets.slice(0, 8));
    } else {
      const filtered = popularAssets.filter(
        asset => 
          asset.symbol.toLowerCase().includes(symbol.toLowerCase()) ||
          asset.name.toLowerCase().includes(symbol.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 6));
    }
  }, [symbol]);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!symbol || symbol.length < 1) {
        setQuote(null);
        return;
      }
      
      setQuoteLoading(true);
      try {
        const data = await PaperTradingService.getQuote(symbol);
        setQuote(data);
        setError('');
      } catch (err) {
        setQuote(null);
      }
      setQuoteLoading(false);
    };

    const timer = setTimeout(fetchQuote, 300);
    return () => clearTimeout(timer);
  }, [symbol]);

  const handleSelectSuggestion = (asset) => {
    setSymbol(asset.symbol);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!symbol || !quantity || !quote) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let actualQuantity = parseFloat(quantity);
      
      if (inputMode === 'dollars') {
        actualQuantity = parseFloat(quantity) / quote.price;
      }

      await PaperTradingService.executeTrade({
        symbol: symbol.toUpperCase(),
        type,
        quantity: actualQuantity,
        price: quote.price
      });

      setSuccess(`Successfully ${type === 'buy' ? 'bought' : 'sold'} ${actualQuantity.toFixed(4)} ${symbol.toUpperCase()}`);
      setQuantity('');
      onTradeComplete();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
    
    setLoading(false);
  };

  const estimatedTotal = quote && quantity ? quote.price * parseFloat(quantity) : 0;
  const estimatedShares = quote && quantity && inputMode === 'dollars' ? parseFloat(quantity) / quote.price : 0;

  return (
    <div className="trade-panel">
      <h3><DollarSign size={18} /> Quick Trade</h3>
      
      <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
        <div className="type-toggle">
          <button
            type="button"
            className={`type-btn buy ${type === 'buy' ? 'active' : ''}`}
            onClick={() => setType('buy')}
          >
            Buy
          </button>
          <button
            type="button"
            className={`type-btn sell ${type === 'sell' ? 'active' : ''}`}
            onClick={() => setType('sell')}
          >
            Sell
          </button>
        </div>

        <div className="form-group">
          <label>Symbol</label>
          <div className="symbol-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onFocus={() => setShowSuggestions(true)}
              placeholder="AAPL, BTC-USD..."
            />
            {symbol && (
              <button 
                type="button" 
                className="clear-btn"
                onClick={() => { setSymbol(''); setQuote(null); }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          {showSuggestions && searchResults.length > 0 && (
            <div className="suggestions-dropdown">
              {searchResults.map((asset) => (
                <button
                  key={asset.symbol}
                  type="button"
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(asset)}
                >
                  <span className="suggestion-symbol">{asset.symbol}</span>
                  <span className="suggestion-name">{asset.name}</span>
                  <span className={`suggestion-type ${asset.type}`}>{asset.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {quote && (
          <div className="quote-display">
            <div className="quote-price">
              {quoteLoading ? (
                <RefreshCw size={16} className="spinning" />
              ) : (
                <>
                  <span className="current-price">{formatCurrency(quote.price)}</span>
                  <span className={`price-change ${quote.changePercent >= 0 ? 'positive' : 'negative'}`}>
                    {quote.changePercent >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {formatPercent(quote.changePercent)}
                  </span>
                </>
              )}
            </div>
            <button 
              type="button" 
              className="view-chart-btn"
              onClick={() => navigate(`/chart/${symbol}`)}
            >
              <ExternalLink size={12} />
              Chart
            </button>
          </div>
        )}

        <div className="form-group">
          <label>Amount</label>
          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-toggle-btn ${inputMode === 'shares' ? 'active' : ''}`}
              onClick={() => { setInputMode('shares'); setQuantity(''); }}
            >
              Shares
            </button>
            <button
              type="button"
              className={`mode-toggle-btn ${inputMode === 'dollars' ? 'active' : ''}`}
              onClick={() => { setInputMode('dollars'); setQuantity(''); }}
            >
              Dollars
            </button>
          </div>
          <div className="quantity-input-wrapper">
            {inputMode === 'dollars' && <span className="input-prefix">$</span>}
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={inputMode === 'shares' ? '0' : '0.00'}
              min="0.01"
              step={inputMode === 'shares' ? '0.01' : '1'}
            />
          </div>
        </div>

        {quantity && quote && parseFloat(quantity) > 0 && (
          <div className="estimated-total">
            {inputMode === 'shares' ? (
              <>
                <span>Estimated Total</span>
                <span className="total-value">{formatCurrency(estimatedTotal)}</span>
              </>
            ) : (
              <>
                <span>Estimated Shares</span>
                <span className="total-value">{estimatedShares.toFixed(estimatedShares < 1 ? 4 : 2)}</span>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="trade-message error">
            <XCircle size={16} />
            {error}
          </div>
        )}
        {success && (
          <div className="trade-message success">
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        <button
          type="submit"
          className={`trade-submit ${type}`}
          disabled={!symbol || !quantity || !quote || loading || parseFloat(quantity) <= 0}
        >
          {loading ? 'Processing...' : `${type === 'buy' ? 'Buy' : 'Sell'} ${symbol || 'Stock'}`}
        </button>
      </form>
    </div>
  );
}

// =============================================================================
// PORTFOLIO SUMMARY
// =============================================================================
function PortfolioSummary({ portfolio, onRefresh, refreshing }) {
  if (!portfolio) return null;

  const isProfit = portfolio.totalReturn >= 0;

  return (
    <div className="portfolio-summary">
      <div className="summary-header">
        <h3><Wallet size={18} /> Portfolio Summary</h3>
        <div className="summary-header-actions">
          <button 
            className="refresh-btn" 
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <div className="summary-grid">
        <div className="summary-card total-value">
          <span className="card-label">Total Value</span>
          <span className="card-value">{formatCurrency(portfolio.totalValue)}</span>
          <span className={`card-change ${isProfit ? 'positive' : 'negative'}`}>
            {isProfit ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {formatCurrency(portfolio.totalReturn)} ({formatPercent(portfolio.totalReturnPercent)})
          </span>
        </div>

        <div className="summary-card">
          <span className="card-label">Cash Balance</span>
          <span className="card-value">{formatCurrency(portfolio.cashBalance)}</span>
        </div>

        <div className="summary-card">
          <span className="card-label">Invested</span>
          <span className="card-value">{formatCurrency(portfolio.totalMarketValue)}</span>
        </div>

        <div className="summary-card">
          <span className="card-label">Unrealized P&L</span>
          <span className={`card-value ${portfolio.totalGainLoss >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(portfolio.totalGainLoss)}
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// POSITIONS LIST
// =============================================================================
function PositionsList({ positions, onSelectSymbol, onQuickTrade }) {
  const navigate = useNavigate();

  if (!positions || positions.length === 0) {
    return (
      <div className="positions-empty">
        <Wallet size={32} />
        <p>No positions yet</p>
        <span>Make your first paper trade to get started</span>
      </div>
    );
  }

  return (
    <div className="positions-list">
      <h3><BarChart3 size={18} /> Positions ({positions.length})</h3>
      
      <div className="positions-table">
        <div className="positions-header">
          <span>Symbol</span>
          <span>Shares</span>
          <span>Avg Cost</span>
          <span>Current</span>
          <span>P&L</span>
          <span>Day</span>
          <span>Actions</span>
        </div>
        
        {positions.map((pos) => (
          <div 
            key={pos.symbol} 
            className="position-row"
          >
            <span className="position-symbol">{pos.symbol}</span>
            <span>{pos.quantity.toFixed(2)}</span>
            <span>{formatCurrency(pos.avg_cost)}</span>
            <span>{formatCurrency(pos.currentPrice)}</span>
            <span className={pos.gainLoss >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(pos.gainLoss)}
              <small>({formatPercent(pos.gainLossPercent)})</small>
            </span>
            <span className={pos.dayChangePercent >= 0 ? 'positive' : 'negative'}>
              {formatPercent(pos.dayChangePercent)}
            </span>
            <span className="position-actions">
              <button 
                className="action-btn trade-btn"
                onClick={() => onQuickTrade && onQuickTrade(pos.symbol)}
                title="Quick Trade"
              >
                Trade
              </button>
              <button 
                className="action-btn chart-btn"
                onClick={() => navigate(`/chart/${pos.symbol}`)}
                title="View Chart"
              >
                <ExternalLink size={14} />
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// TRANSACTION HISTORY
// =============================================================================
function TransactionHistory({ transactions }) {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="history-empty">
        <History size={32} />
        <p>No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="transaction-history">
      <h3><History size={18} /> Recent Transactions</h3>
      
      <div className="transactions-list">
        {transactions.slice(0, 20).map((tx) => (
          <div key={tx.id} className={`transaction-item ${tx.type}`}>
            <div className="tx-icon">
              {tx.type === 'buy' ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
            </div>
            <div className="tx-details">
              <span className="tx-action">
                {tx.type === 'buy' ? 'Bought' : 'Sold'} {tx.quantity} {tx.symbol}
              </span>
              <span className="tx-time">
                {new Date(tx.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="tx-amount">
              <span className={tx.type === 'buy' ? 'negative' : 'positive'}>
                {tx.type === 'buy' ? '-' : '+'}{formatCurrency(tx.totalAmount)}
              </span>
              <span className="tx-price">@ {formatCurrency(tx.price)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// PERFORMANCE STATS
// =============================================================================
function PerformanceStats({ stats }) {
  if (!stats) return null;

  return (
    <div className="performance-stats">
      <h3><Trophy size={18} /> Performance</h3>
      
      <div className="stats-grid">
        <div className="stat-card">
          <Target size={20} />
          <span className="stat-label">Total Trades</span>
          <span className="stat-value">{stats.totalTrades}</span>
        </div>
        
        <div className="stat-card">
          <Percent size={20} />
          <span className="stat-label">Win Rate</span>
          <span className="stat-value">{stats.winRate.toFixed(1)}%</span>
        </div>
        
        <div className="stat-card">
          <TrendingUp size={20} />
          <span className="stat-label">Total Return</span>
          <span className={`stat-value ${stats.totalReturn >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(stats.totalReturnPercent)}
          </span>
        </div>
        
        <div className="stat-card">
          <Clock size={20} />
          <span className="stat-label">Account Age</span>
          <span className="stat-value">{stats.accountAge} days</span>
        </div>
      </div>

      <div className="stats-details">
        <div className="detail-row">
          <span>Realized P&L</span>
          <span className={stats.realizedPL >= 0 ? 'positive' : 'negative'}>
            {formatCurrency(stats.realizedPL)}
          </span>
        </div>
        <div className="detail-row">
          <span>Unrealized P&L</span>
          <span className={stats.unrealizedPL >= 0 ? 'positive' : 'negative'}>
            {formatCurrency(stats.unrealizedPL)}
          </span>
        </div>
        <div className="detail-row">
          <span>Winning Trades</span>
          <span className="positive">{stats.winningTrades}</span>
        </div>
        <div className="detail-row">
          <span>Losing Trades</span>
          <span className="negative">{stats.losingTrades}</span>
        </div>
      </div>

      {stats.bestTrade && (
        <div className="best-worst-trades">
          <div className="trade-highlight best">
            <span className="highlight-label">Best Trade</span>
            <span className="highlight-symbol">{stats.bestTrade.symbol}</span>
            <span className="highlight-value positive">+{formatCurrency(stats.bestTrade.profit)}</span>
          </div>
          {stats.worstTrade && (
            <div className="trade-highlight worst">
              <span className="highlight-label">Worst Trade</span>
              <span className="highlight-symbol">{stats.worstTrade.symbol}</span>
              <span className="highlight-value negative">{formatCurrency(stats.worstTrade.profit)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// RESET MODAL
// =============================================================================
function ResetModal({ isOpen, onClose, onConfirm, loading }) {
  const [balance, setBalance] = useState('100000');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="reset-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <div className="modal-icon warning">
          <AlertTriangle size={32} />
        </div>
        
        <h3>Reset Paper Trading Account?</h3>
        <p>This will delete all positions, history, and reset your cash. Cannot be undone.</p>
        
        <div className="reset-balance-input">
          <label>Starting Balance</label>
          <div className="balance-input-wrapper">
            <DollarSign size={16} />
            <input
              type="number"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              min="1000"
              max="10000000"
            />
          </div>
        </div>
        
        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button 
            className="confirm-btn danger" 
            onClick={() => onConfirm(parseFloat(balance))}
            disabled={loading}
          >
            {loading ? 'Resetting...' : 'Reset Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAPER TRADING COMPONENT
// =============================================================================
export default function PaperTrading({ onSelectSymbol: externalSelectSymbol }) {
  const [portfolio, setPortfolio] = useState(null);
  const [positions, setPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('positions');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setRefreshing(true);
    
    try {
      const [portfolioData, positionsData, txData, statsData] = await Promise.all([
        PaperTradingService.getPortfolio(),
        PaperTradingService.getPositions(),
        PaperTradingService.getTransactions(),
        PaperTradingService.getStats()
      ]);
      
      setPortfolio(portfolioData.portfolio || portfolioData);
      setPositions(positionsData);
      setTransactions(txData.transactions || txData || []);
      setStats(statsData);
      setError('');
    } catch (err) {
      setError(err.message);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTradeComplete = () => {
    loadData(false);
  };

  const handleReset = async (startingBalance) => {
    setResetLoading(true);
    try {
      await PaperTradingService.resetAccount(startingBalance);
      setShowResetModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
    setResetLoading(false);
  };

  if (loading) {
    return (
      <div className="paper-trading loading">
        <div className="loading-spinner" />
        <p>Loading paper trading...</p>
      </div>
    );
  }

  return (
    <div className="paper-trading">
      {error && (
        <div className="error-banner">
          <AlertTriangle size={16} />
          {error}
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      <div className="paper-trading-layout">
        <div className="left-column">
          <TradePanel 
            onTradeComplete={handleTradeComplete}
            initialSymbol={selectedSymbol}
          />
          <PerformanceStats stats={stats} />
        </div>

        <div className="right-column">
          <PortfolioSummary 
            portfolio={portfolio}
            onRefresh={() => loadData(false)}
            refreshing={refreshing}
          />

          <div className="content-tabs">
            <button
              className={activeTab === 'positions' ? 'active' : ''}
              onClick={() => setActiveTab('positions')}
            >
              <BarChart3 size={16} />
              Positions
            </button>
            <button
              className={activeTab === 'history' ? 'active' : ''}
              onClick={() => setActiveTab('history')}
            >
              <History size={16} />
              History
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'positions' ? (
              <PositionsList 
                positions={positions}
                onSelectSymbol={setSelectedSymbol}
                onQuickTrade={setSelectedSymbol}
              />
            ) : (
              <TransactionHistory transactions={transactions} />
            )}
          </div>
        </div>
      </div>

      <ResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        loading={resetLoading}
      />
    </div>
  );
}